import { useEffect, useCallback, useState } from "react";
import { dataSyncService } from "../services/dataSyncService";
import { ProductInterface } from "../interfaces/ProductInterface";
import { CategoryInterface } from "../interfaces/CategoryInterface";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { setLocalData, setIsLoading, setError } from "../slices/localDataSlice";
import { FilterListInterface } from "../interfaces/FilterListInterface";
import { ProductDetailsDataInterface } from "../interfaces/ProductDetailsDataInterface";
import { BrandingInterface } from "../interfaces/BrandingInterface";

export interface UseLocalDataReturn {
    products: ProductInterface[];
    filters: FilterListInterface[];
    branding: BrandingInterface[];
    categories: CategoryInterface[];
    lastSync: number | null;
    isLoading: boolean;
    error: string | null;
    isOffline: boolean;
    hasSynchronizedData: boolean;
    refreshData: () => Promise<void>;
    getLocalImageUrl: (url: string) => Promise<string | null>;
    getLocalBrandingFileUrl: (url: string) => Promise<string | null>;
    clearAllData: () => Promise<void>;
    checkSynchronizedData: () => Promise<boolean>;
    getProductById: (id: string) => Promise<ProductDetailsDataInterface | null>;
}

export const useLocalData = (): UseLocalDataReturn => {
    const dispatch = useAppDispatch();
    const {
        products,
        filters,
        branding,
        categories,
        lastSync,
        isLoading,
        error,
    } = useAppSelector((state) => state.localData);
    const [isOffline, setIsOffline] = useState(!navigator.onLine);
    const [hasSynchronizedData, setHasSynchronizedData] = useState(false);

    const checkSynchronizedData = useCallback(async (): Promise<boolean> => {
        try {
            const hasSyncedData = await dataSyncService.hasSynchronizedData();
            setHasSynchronizedData(hasSyncedData);
            return hasSyncedData;
        } catch (err) {
            console.error("Failed to check synchronized data:", err);
            setHasSynchronizedData(false);
            return false;
        }
    }, []);

    const loadLocalData = useCallback(async () => {
        dispatch(setIsLoading(true));
        dispatch(setError(null));

        try {
            // Check if we have synchronized data first
            await checkSynchronizedData();

            const localData = await dataSyncService.getLocalData();
            dispatch(setLocalData(localData));

            // Clear any previous errors if data loads successfully
            if (
                localData.products.length > 0 ||
                localData.categories.length > 0
            ) {
                dispatch(setError(null));
            }
        } catch (err) {
            const errorMessage =
                err instanceof Error
                    ? err.message
                    : "Failed to load local data";
            console.warn("Local data loading failed:", errorMessage);

            // Don't show error if we're offline and just can't access IndexedDB initially
            // The app should still work with whatever data is already in the Redux store
            if (!isOffline) {
                dispatch(setError(errorMessage));
            }
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [dispatch, isOffline, checkSynchronizedData]);

    const refreshData = useCallback(async () => {
        await loadLocalData();
    }, [loadLocalData]);

    const getLocalImageUrl = useCallback(
        async (url: string): Promise<string | null> => {
            try {
                return await dataSyncService.getLocalImageUrl(url);
            } catch (err) {
                console.error(`Failed to get local image URL for ${url}:`, err);
                return null;
            }
        },
        [],
    );

    const getLocalBrandingFileUrl = useCallback(
        async (url: string): Promise<string | null> => {
            try {
                return await dataSyncService.getLocalBrandingFileUrl(url);
            } catch (err) {
                console.error(
                    `Failed to get local branding file URL for ${url}:`,
                    err,
                );
                return null;
            }
        },
        [],
    );

    const clearAllData = useCallback(async () => {
        try {
            await dataSyncService.clearAllData();
            dispatch(
                setLocalData({
                    products: [],
                    filters: [],
                    branding: [],
                    categories: [],
                    lastSync: null,
                }),
            );
        } catch (err) {
            const errorMessage =
                err instanceof Error ? err.message : "Failed to clear data";
            dispatch(setError(errorMessage));
        }
    }, [dispatch]);

    const getProductById = useCallback(
        async (id: string): Promise<ProductDetailsDataInterface | null> => {
            try {
                return await dataSyncService.getProductById(Number(id));
            } catch (err) {
                console.error(`Failed to get product with ID ${id}:`, err);
                return null;
            }
        },
        [],
    );

    useEffect(() => {
        loadLocalData();
    }, [loadLocalData]);

    // Listen for online/offline events
    useEffect(() => {
        const handleOnline = (): void => setIsOffline(false);
        const handleOffline = (): void => setIsOffline(true);

        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);

        return () => {
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
        };
    }, []);

    return {
        products,
        filters,
        branding,
        categories,
        lastSync,
        isLoading,
        error,
        isOffline,
        hasSynchronizedData,
        refreshData,
        getLocalImageUrl,
        getLocalBrandingFileUrl,
        clearAllData,
        checkSynchronizedData,
        getProductById,
    };
};
